import { format, isToday, isTomorrow, isPast, startOfDay, differenceInCalendarDays } from "date-fns";
import { ArrowUpRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { BellIcon, CheckIcon, ClockIcon, PaperAirplaneIcon } from "@heroicons/react/24/solid";
import { ClipLoader } from "react-spinners";
import { cn, formatAmount } from "@utils";

const BAND_CHIPS = {
  critical: "border-red-600/30 bg-red-50 text-red-700",
  high: "border-amber-600/30 bg-amber-50 text-amber-700",
};

// When the task is, relative for the near term. A task due today shows just its time (it's already
// in today's list); an all-day task (no time → midnight) shows nothing. A past date still flags red
// (overdue), but the wording stays neutral: a task isn't "due", it's just scheduled for a time.
const describeDue = (due) => {
  if (isToday(due)) {
    const time = format(due, "HH:mm");
    return time === "00:00" ? null : { text: time, overdue: false };
  }
  if (isTomorrow(due)) return { text: "Tomorrow", overdue: false };
  if (isPast(startOfDay(due))) {
    const days = differenceInCalendarDays(startOfDay(new Date()), startOfDay(due));
    return { text: `${days} ${days === 1 ? "day" : "days"} overdue`, overdue: true };
  }
  return { text: format(due, "EEE d MMM"), overdue: false };
};

// Why the task ranks where it does, from the BFF's priority reasons — shown as the chip's tooltip.
const bandTooltip = (priority) => priority?.reasons?.join(" · ") || undefined;

const TaskCard = ({
  task, onOpen, onPreview, onSend, onComplete, onDismiss, variant = "outstanding",
  sending = false, previewing = false, showAccountLink = false,
}) => {
  // Preview/Send drive the payment-request reminder flow, so they need the request id. A plain
  // to-do (no request to chase) has no reminder — it just gets a "Mark as done" action instead.
  // Completed tasks show no actions.
  const canAct = task.paymentRequestId != null;
  // An IncomingPaymentReview task has no payment request yet — the request was never sent — so it
  // acts off the originating action instead, with a single one-click "Send payment request" button.
  const isReview = task.taskType === "IncomingPaymentReview";
  const canReview = isReview && task.actionId != null;
  const isCompleted = variant === "completed";
  const isSnoozed = variant === "snoozed";
  const title = task.customerName || task.title;
  // The card leads with the customer name; surface the task's own title underneath as the "what to do".
  // Missed-payment tasks carry a phrased description instead, so leave those to it; a reminder that has
  // no customer is already led by its title, so don't repeat it.
  const reminderTitle = !task.taskType && task.title !== title ? task.title : null;
  // Some tasks carry the same text as both title and description — don't print it twice.
  const description =
    task.description &&
    task.description !== reminderTitle &&
    task.description !== title
      ? task.description
      : null;
  const bandChip = !isCompleted && BAND_CHIPS[task.priority?.band];
  const TagIcon = task.tagIcon;
  // Due is shown on active cards only; snoozed/completed already carry their own status line.
  const due = !isCompleted && !isSnoozed && task.due ? describeDue(task.due) : null;
  // While a send/preview for this card is in flight, lock its actions so it can't be double-fired.
  const busy = sending || previewing;
  // The page only passes onOpen when there's something to open (a customer / plan), so a card without
  // one mustn't look or behave clickable.
  const openable = !!onOpen;

  return (
    <div
      {...(openable && {
        role: "button",
        tabIndex: 0,
        onClick: () => onOpen(task),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(task);
          }
        },
      })}
      className={cn(
        "group relative flex flex-col rounded-3xl border border-zinc-200 bg-white px-6 py-6 text-left shadow-md transition-all",
        openable &&
          "cursor-pointer hover:border-zinc-300 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500",
        isSnoozed && "opacity-70",
      )}
    >
      {!isCompleted && (
        <button
          type="button"
          aria-label="Dismiss suggestion"
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss?.(task);
          }}
          className="absolute right-4 top-4 text-zinc-400 opacity-0 transition-opacity hover:text-zinc-600 focus-visible:opacity-100 group-hover:opacity-100 disabled:cursor-not-allowed"
        >
          <XMarkIcon aria-hidden="true" className="size-4 stroke-2" />
        </button>
      )}

      {/* Badge on its own line, then name / description / time stacked; the amount pins top-right. */}
      <div className="flex items-start justify-between gap-4 pr-6">
        <div className="flex min-w-0 flex-col gap-1.5">
          {(task.tag || bandChip) && (
            <div className="mb-1.5 flex items-center gap-2">
              {task.tag && (
                <span className={cn("inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-semibold", task.tagStyle)}>
                  {TagIcon && <TagIcon aria-hidden="true" className="size-3.5" />}
                  {task.tag}
                </span>
              )}
              {bandChip && (
                <span
                  title={bandTooltip(task.priority)}
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${bandChip}`}
                >
                  {task.priority.band}
                </span>
              )}
            </div>
          )}
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="min-w-0 truncate text-base font-bold text-zinc-800">{title}</span>
            {openable && showAccountLink && task.customerId && (
              <ArrowUpRightIcon aria-hidden="true" className="size-3.5 shrink-0 stroke-2 text-zinc-400 group-hover:text-zinc-600" />
            )}
          </span>
          {/* One context line: the task's own title if it has one, otherwise its description. */}
          {(reminderTitle || description) && (
            <div className="text-sm text-zinc-600">{reminderTitle || description}</div>
          )}
          {due && (
            <div
              className={`flex items-center gap-1.5 text-[13px] ${
                due.overdue ? "text-red-600" : "text-zinc-600"
              }`}
            >
              <ClockIcon aria-hidden="true" className="size-3.5" />
              <span className="font-semibold">{!due.overdue ? "Today" : undefined} {due.text}</span>
            </div>
          )}
          {isSnoozed && task.snoozedUntil && (
            <div className="text-[13px] text-zinc-500">
              Snoozed until {format(task.snoozedUntil, "EEE d MMM, HH:mm")}
            </div>
          )}
          {isCompleted && task.completedAt && (
            <div className="text-[13px] text-zinc-500">
              Completed {format(task.completedAt, "HH:mm")}
              {task.completedByUserId === "system" ? " — payment received" : ""}
            </div>
          )}
        </div>
        {task.amount != null && (
          <div className="shrink-0 text-right leading-tight">
            <div className="text-[11px] font-medium text-zinc-400">At risk</div>
            <div className="text-lg font-semibold text-zinc-900">{formatAmount(task.amount)}</div>
          </div>
        )}
      </div>

      {!isCompleted && (
        <div className="mt-4 flex items-center justify-between gap-4" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3">
            {canAct ? (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSend?.(task)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-zinc-900 px-4 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-zinc-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:bg-zinc-300"
                >
                  {sending ? (
                    <>
                      <ClipLoader size={14} color="#fff" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <BellIcon aria-hidden="true" className="size-3.5" />
                      Send reminder
                    </>
                  )}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onPreview?.(task)}
                  className="inline-flex items-center gap-2 text-[13px] font-semibold text-zinc-900 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:text-zinc-300 disabled:hover:text-zinc-300"
                >
                  {previewing && <ClipLoader size={14} color="currentColor" aria-hidden="true" />}
                  Preview
                </button>
              </>
            ) : canReview ? (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onSend?.(task)}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-zinc-900 px-4 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-zinc-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:bg-zinc-300"
                >
                  {sending ? (
                    <>
                      <ClipLoader size={14} color="#fff" aria-hidden="true" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon aria-hidden="true" className="size-3.5" />
                      Send anyway
                    </>
                  )}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onDismiss?.(task)}
                  className="inline-flex items-center gap-2 text-[13px] font-semibold text-zinc-900 hover:text-zinc-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:text-zinc-300 disabled:hover:text-zinc-300"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => onComplete?.(task)}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-zinc-900 px-4 py-1.5 text-[13px] font-medium text-white transition-colors hover:bg-zinc-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                <CheckIcon aria-hidden="true" className="size-4 stroke-current stroke-2" />
                Mark as done
              </button>
            )}
          </div>
          {openable && (
            <span className="text-xs text-zinc-400 opacity-0 transition-opacity group-hover:opacity-100">
              Click to view context
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskCard;
